import * as jwtDecodeModule from 'jwt-decode';

type JwtDecodeFn = <T>(token: string) => T;

const jwtDecodeExport = (jwtDecodeModule as { jwtDecode?: JwtDecodeFn }).jwtDecode ??
  (jwtDecodeModule as { default?: JwtDecodeFn }).default;

if (typeof jwtDecodeExport !== 'function') {
  throw new Error('jwt-decode export is unavailable');
}

const jwtDecode: JwtDecodeFn = jwtDecodeExport;

/**
 * Returns true when a bearer token must not be sent to the API.
 *
 * JWT-shaped strings with invalid encoding or JSON are rejected so arbitrary
 * localStorage values are not treated as valid sessions.
 *
 * JWTs with an `exp` claim in the past are rejected without a round trip.
 * Tokens that are not three-part JWTs are left to the server (opaque tokens).
 *
 * Signature verification is not performed client-side; callers must still
 * validate the session with the API.
 */
export function shouldRejectClientSideToken (token: string): boolean {
  const parts = token.split('.');

  if (parts.length !== 3) {
    return false;
  }

  try {
    const payload = jwtDecode<{ exp?: number }>(token);

    if (payload.exp == null) {
      return false;
    }

    return payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}
